import { getPriceForPart } from "./getPriceForPart";
import type { PartLike, PriceData } from "./types";

export const RIVET_MIN_OFFSET = 75;
export const RIVET_DISTANCE = 150;

/**
 * Locks and hinges produce two part definitions per physical part (base + lid).
 * The lid copy has `top === true` and must be excluded from counting.
 */
export function isDuplicateSupplyPart(props: PartLike): boolean {
  return (
    (props.type === "Lock" || props.type === "Hinge") && props.top === true
  );
}

/**
 * Calculate the number of individual rivets for an angle section.
 * Each position has a rivet pair (top + bottom), so positions × 2.
 */
export function calcAngleSectionRivetCount(length: number): number {
  if (length < 2 * RIVET_MIN_OFFSET) return 0;
  const positions =
    Math.floor((length - 2 * RIVET_MIN_OFFSET) / RIVET_DISTANCE) + 1;
  return positions * 2;
}

/**
 * Calculate rivet positions for a given length.
 * Used by the 3D renderer and PDF generation.
 * @param length - Length in mm
 * @returns Array of rivet positions (relative to center, in mm)
 */
export function calculateRivetPositions(length: number): number[] {
  const numRivets = calcAngleSectionRivetCount(length) / 2;
  if (numRivets === 0) return [];
  const offset = -RIVET_DISTANCE * ((numRivets - 1) / 2);
  return Array.from(
    { length: numRivets },
    (_, i) => offset + i * RIVET_DISTANCE,
  );
}

/**
 * Returns the distance from the first rivet to the outer edge of the profile.
 * Used for display in PDF angle section tables.
 * @param length - Length in mm
 * @returns String describing the rivet offset from edge
 */
export function calculateRivetSpacing(length: number): string {
  const positions = calculateRivetPositions(length);
  if (positions.length === 0) return "-";
  const firstRivetPosition = Math.min(...positions);
  const leftEdge = -length / 2;
  const offsetFromEdge = firstRivetPosition - leftEdge;
  return offsetFromEdge.toFixed(1);
}

/**
 * Count total rivets for a list of parts.
 * - AngleSections: calculated from length (positions × 2)
 * - Other parts: looked up from PriceData[code].CountRivets (if priceData provided)
 *
 * Single source of truth used by calculatePrice, CSV export, and PDF.
 */
export function countTotalRivets(
  parts: PartLike[],
  priceData?: PriceData,
): number {
  let total = 0;

  for (const part of parts) {
    const props = part.properties ?? part;

    if (props.dontShowInPlan) continue;

    if (props.type === "AngleSection") {
      if (typeof props.length === "number") {
        total += calcAngleSectionRivetCount(props.length);
      }
      continue;
    }

    if (isDuplicateSupplyPart(props)) continue;
    if (!priceData) continue;

    const info = getPriceForPart(part);
    if (!info) continue;

    const entry = priceData[info.code];
    if (entry && entry.CountRivets > 0) {
      total += entry.CountRivets;
    }
  }

  return total;
}
